/**
 * Pool > Logs tab.
 *
 * On-demand viewer for the SkyServe per-service controller / LB log.
 * Hits ``GET /pools/{id}/skyserve-logs?target=...`` which proxies into
 * ``sky.serve.tail_logs(target=...)`` on the controller. The log file
 * itself lives on the **SkyServe controller VM** (a separate VM that
 * SkyPilot provisions to drive replica lifecycle, NOT the backstage
 * controller pod) — only this RPC can reach it.
 *
 * Mostly useful when a pool gets stuck in CONTROLLER_FAILED: the
 * service-controller daemon on the controller VM has crashed before
 * ``localhost:20001`` could come up, and its crash trace is the only
 * record of *why*. We default to ``controller`` because that's the
 * 90% case; ``load_balancer`` is exposed for the rarer "service is up
 * but routing is broken" debugging path.
 *
 * Used to live as a `sky logs` toggle inside the Pool > Replicas tab
 * header. Promoting it to its own SubNav tab makes it discoverable
 * (operators no longer have to know to click an obscure overflow
 * item) and separates the raw controller-log view from the
 * structured event stream on the Events tab — two genuinely
 * different debugging surfaces.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Download, RefreshCw, Search, Terminal, X } from "lucide-react";

import { api } from "@/api/client";
import type { Pool, Replica } from "@/api/types";
import { Section } from "@/components/Section";

interface Props {
  pool: Pool;
  replicas?: Replica[];
  /**
   * Optional initial target to open the viewer with — used when the
   * Replicas tab links to "Open logs for replica#42" so the operator
   * lands directly on the right log without an extra click.
   */
  initialTarget?: SkyServeLogTarget;
}

/**
 * Either of the two SkyServe-controller-side logs (per-service
 * controller / load balancer) or a per-replica setup+run log. Replica
 * logs encode the SkyServe replica id (the integer that cluster
 * names embed as ``…-p2-<id>``) so the controller can route the
 * fetch through ``sky.serve.tail_logs(target='replica',
 * replica_id=N)``.
 */
type SkyServeLogTarget =
  | "controller"
  | "load_balancer"
  | { kind: "replica"; sky_replica_id: number; replica_id: number };

function targetParam(t: SkyServeLogTarget): string {
  if (typeof t === "string") return t;
  return `replica:${t.sky_replica_id}`;
}

function targetKey(t: SkyServeLogTarget): string {
  return typeof t === "string" ? t : `replica:${t.sky_replica_id}`;
}
type LineSeverity = "error" | "warn" | "info" | "plain";

// Severity classification is heuristic — these logs are a mix of
// Python stdlib logging, sky-internal stderr, and bare prints from
// user setup scripts, so there is no single canonical format. We tag
// "error" generously (incl. tracebacks and HTTP 4xx/5xx mentions)
// because operators land here specifically to find failure causes;
// false-positive coloring is cheap, missing a real error is not.
const ERROR_RE =
  /(\bERROR\b|\bERR\b|\bFATAL\b|\bCRITICAL\b|\bFAILED\b|\bFailure\b|\bException\b|\bTraceback\b|\bunrecoverable\b|\bnot\s+found\b|\b(?:4\d\d|5\d\d)\s+(?:Client|Server)?\s*Error\b|\b(?:Repository|Connection|Permission)\s+(?:Not\s+Found|Refused|Denied)\b|\braise(?:d)?\s+[A-Z][A-Za-z]*Error\b)/;
const WARN_RE = /(\bWARN(?:ING)?\b|\bDeprecat|\bRetry(?:ing)?\b|\bTimeoutError\b|\bResourcesUnavailable)/;
const INFO_RE = /(\bINFO\b|\bsky\.serve\.up\.ok\b|\b✔\b|\bREADY\b|\bSUCCESS\b)/;

function classifyLine(line: string): LineSeverity {
  if (ERROR_RE.test(line)) return "error";
  if (WARN_RE.test(line)) return "warn";
  if (INFO_RE.test(line)) return "info";
  return "plain";
}

// Strip ANSI color escape sequences that SkyPilot/sky-serve embed in
// stdout (e.g. ``\x1b[33m↺\x1b[0m``). They render as garbage in HTML
// and would also confuse the severity regexes above.
const ANSI_RE = /\x1b\[[0-9;]*m/g;

function stripAnsi(s: string): string {
  return s.replace(ANSI_RE, "");
}

function renderWithMatches(line: string, query: string): ReactNode {
  if (!query) return line;
  const lower = line.toLowerCase();
  const q = query.toLowerCase();
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < line.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      out.push(line.slice(i));
      break;
    }
    if (idx > i) out.push(line.slice(i, idx));
    out.push(
      <mark key={key++} className="skylog__match">
        {line.slice(idx, idx + q.length)}
      </mark>,
    );
    i = idx + q.length;
  }
  return out;
}

export function PoolLogs({ pool, replicas, initialTarget }: Props) {
  // URL-driven deep links (used by the Events tab's "Open logs at this
  // frame" button) layer two optional params on top of the existing
  // ``?focus=pool:N&tab=logs`` shape:
  //
  //   - ``replica=<sky_replica_id>`` — preselect that replica's log,
  //     same as if the operator picked it from the dropdown.
  //   - ``q=<string>`` — preset the search filter (URL-decoded). The
  //     events deep-link feeds the traceback frame text in here so
  //     the operator lands directly on the matching line in the raw
  //     log dump.
  //
  // Both are resolved once on the initial render, then handed off to
  // local state so the operator can keep navigating (changing the
  // dropdown / clearing search) without the URL fighting them. They
  // re-apply if a *new* deep link is followed (e.g. operator hits
  // back, then opens a different event).
  const [searchParams] = useSearchParams();
  const urlReplica = searchParams.get("replica");
  const urlQuery = searchParams.get("q") ?? "";

  const resolveUrlTarget = (): SkyServeLogTarget | null => {
    if (!urlReplica) return null;
    const sid = Number(urlReplica);
    if (!Number.isFinite(sid)) return null;
    const hit = (replicas ?? []).find((r) => r.sky_replica_id === sid);
    if (!hit) return null;
    return {
      kind: "replica",
      sky_replica_id: sid,
      replica_id: hit.id,
    };
  };

  const [target, setTarget] = useState<SkyServeLogTarget>(
    () => resolveUrlTarget() ?? initialTarget ?? "controller",
  );
  // When the operator deep-links to a replica from the Replicas tab,
  // ``initialTarget`` updates after mount — sync the state so the
  // viewer follows. We only react when the *parent's* selected
  // replica changes (string-keyed comparison) so re-renders from
  // unrelated Pool prop churn don't kick the operator back to a
  // replica they manually navigated away from.
  const initialKey = initialTarget ? targetKey(initialTarget) : null;
  useEffect(() => {
    if (initialTarget) setTarget(initialTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);
  // Same idea for URL-driven deep links: react when the URL params
  // *change* (operator clicked a different "Open logs" button)
  // rather than every render. We also need ``replicas`` in the dep
  // list because the lookup table starts empty and fills in after
  // the per-pool query resolves — without re-running once it lands,
  // a deep link followed before replicas loaded would silently fall
  // back to ``controller``.
  useEffect(() => {
    const t = resolveUrlTarget();
    if (t) setTarget(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlReplica, replicas]);
  const [query, setQuery] = useState(urlQuery);
  useEffect(() => {
    if (urlQuery) setQuery(urlQuery);
  }, [urlQuery]);
  const q = useQuery<string>({
    queryKey: ["pool", pool.id, "skyserve-logs", targetKey(target)],
    queryFn: () =>
      api.getText(
        `/pools/${pool.id}/skyserve-logs?target=${encodeURIComponent(
          targetParam(target),
        )}`,
      ),
    // The underlying call SSH-tunnels into the SkyServe controller VM;
    // each fetch costs real round-trip time + reuses a paramiko
    // connection. We deliberately do NOT auto-refetch — the operator
    // hits the refresh button when they want a fresh dump.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const rawText = q.data ?? "";
  const isError = q.isError;

  // Pre-classify lines once per fetch. We keep the original line
  // index so the visible row numbering stays stable when the search
  // filter hides non-matching lines (operators sometimes copy "line
  // 1234" back to terminal greps).
  const lines = useMemo(() => {
    if (!rawText) return [] as { idx: number; text: string; sev: LineSeverity }[];
    return stripAnsi(rawText)
      .split("\n")
      .map((text, idx) => ({ idx, text, sev: classifyLine(text) }));
  }, [rawText]);

  const filtered = useMemo(() => {
    if (!query) return lines;
    const lc = query.toLowerCase();
    return lines.filter((l) => l.text.toLowerCase().includes(lc));
  }, [lines, query]);

  const counts = useMemo(() => {
    let err = 0;
    let warn = 0;
    for (const l of lines) {
      if (l.sev === "error") err++;
      else if (l.sev === "warn") warn++;
    }
    return { err, warn, total: lines.length };
  }, [lines]);

  // Title / download-filename slug for whatever target is active.
  // ``replica:<id>`` ends up as ``replica-<id>`` so filenames stay
  // shell-safe (no ``:`` which Windows refuses).
  const targetLabel: string =
    typeof target === "string"
      ? target
      : `replica#${target.replica_id}`;
  const targetSlug: string =
    typeof target === "string" ? target : `replica-${target.sky_replica_id}`;

  // Replica picker entries: SkyServe replica id (the integer the
  // backend keys ``sky.serve.tail_logs`` on) plus the BackStage
  // replica.id for label display. Filter out replicas that have no
  // ``sky_replica_id`` yet (early provisioning, before SkyServe has
  // assigned one) — there's nothing for the controller to fetch.
  const replicaTargets = useMemo(() => {
    if (!replicas?.length)
      return [] as Array<{
        sky_replica_id: number;
        replica_id: number;
        status: string;
      }>;
    return replicas
      .filter((r): r is Replica & { sky_replica_id: number } =>
        r.sky_replica_id != null,
      )
      .map((r) => ({
        sky_replica_id: r.sky_replica_id,
        replica_id: r.id,
        status: r.status,
      }))
      .sort((a, b) => a.replica_id - b.replica_id);
  }, [replicas]);

  const handleDownload = () => {
    // Save the un-stripped original text so operators can pipe it
    // back into terminals that *do* render ANSI (sky logs colors
    // remain useful in a real shell).
    const blob = new Blob([rawText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `${pool.sky_service_name}.${targetSlug}.${ts}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isReplicaTarget = typeof target !== "string";

  return (
    <Section
      title={`SkyServe ${targetLabel} log · ${pool.sky_service_name}`}
      icon={<Terminal />}
      actions={
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <span className="skylog__toggle" role="group" aria-label="log target">
            <button
              type="button"
              className={`btn btn--ghost btn--sm${
                target === "controller" ? " is-active" : ""
              }`}
              aria-pressed={target === "controller"}
              onClick={() => setTarget("controller")}
              title="Per-service controller process — drives replica lifecycle on the SkyServe controller VM."
            >
              controller
            </button>
            <button
              type="button"
              className={`btn btn--ghost btn--sm${
                target === "load_balancer" ? " is-active" : ""
              }`}
              aria-pressed={target === "load_balancer"}
              onClick={() => setTarget("load_balancer")}
              title="SkyServe load balancer — round-robins between READY replicas."
            >
              load_balancer
            </button>
            {replicaTargets.length > 0 && (
              <select
                className={`btn btn--ghost btn--sm${
                  isReplicaTarget ? " is-active" : ""
                }`}
                value={
                  isReplicaTarget
                    ? String(
                        (target as { sky_replica_id: number }).sky_replica_id,
                      )
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const sid = Number(v);
                  const hit = replicaTargets.find(
                    (r) => r.sky_replica_id === sid,
                  );
                  if (hit) {
                    setTarget({
                      kind: "replica",
                      sky_replica_id: hit.sky_replica_id,
                      replica_id: hit.replica_id,
                    });
                  }
                }}
                title="Per-replica setup + run log — the workload's own stdout/stderr (where Python tracebacks live)."
                style={{ paddingInline: 6 }}
              >
                <option value="">replica…</option>
                {replicaTargets.map((r) => (
                  <option
                    key={r.sky_replica_id}
                    value={String(r.sky_replica_id)}
                  >
                    replica#{r.replica_id} ({r.status})
                  </option>
                ))}
              </select>
            )}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={handleDownload}
            disabled={!rawText || q.isFetching}
            title="Download the raw log (ANSI escapes preserved) as a .log file"
          >
            <Download size={11} />
            download
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void q.refetch()}
            disabled={q.isFetching}
            title="Re-fetch from the SkyServe controller VM"
          >
            <RefreshCw size={11} />
            {q.isFetching ? "fetching…" : "refresh"}
          </button>
        </span>
      }
      flush
    >
      <div className="skylog">
        <div className="skylog__searchbar">
          <label className="skylog__search">
            <Search size={12} aria-hidden />
            <input
              type="search"
              placeholder="Filter lines (case-insensitive)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                className="skylog__search-clear"
                aria-label="Clear search"
                onClick={() => setQuery("")}
              >
                <X size={11} />
              </button>
            )}
          </label>
          <span className="skylog__stats">
            {query
              ? `${filtered.length} / ${counts.total} lines`
              : `${counts.total} lines`}
            {counts.err > 0 && (
              <span className="skylog__stat skylog__stat--err">
                {counts.err} error{counts.err === 1 ? "" : "s"}
              </span>
            )}
            {counts.warn > 0 && (
              <span className="skylog__stat skylog__stat--warn">
                {counts.warn} warn{counts.warn === 1 ? "" : "s"}
              </span>
            )}
          </span>
        </div>
        <div className="skylog__body">
          {q.isLoading ? (
            <pre className="skylog__pre skylog__pre--message">
              fetching from SkyServe controller VM…
            </pre>
          ) : isError ? (
            <pre className="skylog__pre skylog__pre--message">
              {`# fetch failed: ${
                (q.error as Error | undefined)?.message ?? "unknown error"
              }\n# (the SkyServe controller VM may be offline, or this pool's service hasn't been registered yet)`}
            </pre>
          ) : filtered.length === 0 ? (
            <pre className="skylog__pre skylog__pre--message">
              {rawText ? "(no matching lines)" : "(empty)"}
            </pre>
          ) : (
            <pre className="skylog__pre">
              {filtered.map((l) => (
                <div
                  key={l.idx}
                  className={`skylog__line skylog__line--${l.sev}`}
                >
                  {renderWithMatches(l.text, query) || "\u00a0"}
                </div>
              ))}
            </pre>
          )}
        </div>
      </div>
    </Section>
  );
}
